import React, { useState, useMemo } from "react";
import { 
  Box, IconButton, Stack, Typography, Button, 
  MenuItem, Select, FormControl 
} from "@mui/material";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import TuneIcon from '@mui/icons-material/Tune'; 
import useMediaQuery from "@mui/material/useMediaQuery";

export default function Blog({ listPost, setPage, totalPage, currentPage }) {
  const [viewMode, setViewMode] = useState("list");
  const [sortBy, setSortBy] = useState("newest");
  const isMobile = useMediaQuery("(max-width:600px)");

  // --- SORTING LOGIC ---
  // Ginagamit ang useMemo para hindi mag-recompute ang sorting 
  // maliban na lang kung nagbago ang listPost o ang sortBy state.
  const sortedPosts = useMemo(() => {
    if (!listPost) return [];
    
    return [...listPost].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);

      if (sortBy === "newest") {
        return dateB - dateA; // Pinakabago muna
      } else {
        return dateA - dateB; // Pinakaluma muna
      }
    });
  }, [listPost, sortBy]);

  const filterBoxStyle = {
    border: "1px solid #e0e0e0",
    display: "flex",
    alignItems: "center",
    px: 2,
    py: 1,
    height: "45px",
    cursor: "pointer",
    transition: "0.3s",
    "&:hover": { bgcolor: "#f9f9f9" }
  };

  return (
    <Box sx={{ width: "100%", bgcolor: "#ffffff" }}>
      
      
      <Box sx={{ 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 1,
        mb: 6,
        pb: 2,
        borderBottom: "1px solid #eee"
      }}>
        
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              sx={{ 
                borderRadius: 0, 
                height: "45px", 
                fontSize: "0.85rem", 
                fontWeight: 700,
                ".MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#000" }
              }}
            >
              <MenuItem value="newest">Sort By: Newest</MenuItem>
              <MenuItem value="oldest">Sort By: Oldest</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        <Stack direction="row" spacing={0}>
          <IconButton 
            onClick={() => setViewMode("grid")} 
            sx={{ 
              borderRadius: 0, 
              border: "1px solid #e0e0e0",
              borderRight: "none",
              bgcolor: viewMode === "grid" ? "#f5f5f5" : "transparent",
              color: viewMode === "grid" ? "#000" : "#ccc",
              width: 45, height: 45
            }}
          >
            <GridViewIcon fontSize="small" />
          </IconButton>
          <IconButton 
            onClick={() => setViewMode("list")} 
            sx={{ 
              borderRadius: 0, 
              border: "1px solid #e0e0e0",
              bgcolor: viewMode === "list" ? "#f5f5f5" : "transparent",
              color: viewMode === "list" ? "#000" : "#ccc",
              width: 45, height: 45
            }}
          >
            <ViewListIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      
      <div className="row g-4">
        {sortedPosts.map((data, key) => {
          const today = new Date(data.date);
          const isListView = viewMode === "list";
          
          let srcURL = "";
          try {
            const media = data._embedded?.["wp:featuredmedia"]?.[0];
            if (media && media.source_url) {
              srcURL = media.source_url.replace("188.166.186.239", "apiblog.leon-gallery.com");
            }
          } catch (error) { console.warn(error); }

          const category = data._embedded?.["wp:term"]?.[0]?.[0]?.name?.toUpperCase() || "STORY";
          const colClass = isListView ? "col-12" : "col-md-6 col-12";

          return (
            <div className={colClass} key={key} style={{ marginBottom: 40 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isListView && !isMobile ? "row" : "column",
                  gap: isListView ? 4 : 0,
                  transition: "0.3s",
                }}
              >
                
                <Box
                  sx={{
                    flexShrink: 0,
                    width: isListView && !isMobile ? "380px" : "100%",
                    height: isListView ? (isMobile ? "250px" : "280px") : "380px",
                    bgcolor: "#ffffff",
                    border: "1px solid #f0f0f0",
                    p: 2, 
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    "&:hover": { borderColor: "#ddd" }
                  }}
                  onClick={() => (window.location.href = `/stories/${data.slug}`)}
                >
                  <Box 
                    sx={{
                      width: "100%",
                      height: "100%",
                      backgroundImage: `url(${srcURL})`,
                      backgroundSize: "contain",
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                    }}
                  />
                </Box>

                
                <Box sx={{ py: 2, flex: 1, px: isListView && !isMobile ? 0 : 1 }}>
                  <Typography variant="caption" sx={{ color: "#999", fontWeight: 800, letterSpacing: 1.5 }}>
                    {today.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} | {category}
                  </Typography>

                  <Typography
                    variant="h5"
                    sx={{
                      my: 1.5,
                      fontFamily: "serif",
                      fontWeight: 700,
                      lineHeight: 1.2,
                      fontSize: isMobile ? "1.2rem" : "1.6rem"
                    }}
                  >
                    <a href={`/stories/${data.slug}`} style={{ color: "#000", textDecoration: "none" }}>
                      {data.title.rendered}
                    </a>
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "#666",
                      mb: 2.5,
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      lineHeight: 1.7
                    }}
                  >
                    {data.excerpt.rendered.replace(/<\/?[^>]+(>|$)/g, "")}
                  </Typography>

                  <Button 
                    href={`/stories/${data.slug}`}
                    sx={{ 
                      color: "#000", 
                      p: 0, 
                      minWidth: 0, 
                      textDecoration: "underline", 
                      fontWeight: 800,
                      fontSize: "12px",
                      "&:hover": { color: "#666", bgcolor: "transparent" }
                    }}
                  >
                    READ ARTICLE
                  </Button>
                </Box>
              </Box>
            </div>
          );
        })}
        
      </div>
    </Box>
  );
}